import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Async thunks
export const get_employees = createAsyncThunk(
  'employee/get_employees',
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get('/employees', { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const create_employee = createAsyncThunk(
  'employee/create_employee',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/employee', info, { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const update_employee = createAsyncThunk(
  'employee/update_employee',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { employeeId, ...updateData } = info;
      const { data } = await api.put(`/employee/${employeeId}`, updateData, { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const delete_employee = createAsyncThunk(
  'employee/delete_employee',
  async (employeeId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.delete(`/employee/${employeeId}`, { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const get_available_modules = createAsyncThunk(
  'employee/get_available_modules',
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get('/available-modules', { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const get_employee_permissions = createAsyncThunk(
  'employee/get_employee_permissions',
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get('/employee-permissions', { withCredentials: true });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const employeeReducer = createSlice({
  name: 'employee',
  initialState: {
    employees: [],
    modules: [],
    permissions: [],
    loader: false,
    successMessage: '',
    errorMessage: ''
  },
  reducers: {
    messageClear: (state) => {
      state.successMessage = '';
      state.errorMessage = '';
    }
  },
  extraReducers: {
    // Get employees
    [get_employees.pending]: (state) => {
      state.loader = true;
    },
    [get_employees.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to fetch employees';
    },
    [get_employees.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.employees = payload.employees;
    },

    // Create employee
    [create_employee.pending]: (state) => {
      state.loader = true;
    },
    [create_employee.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to create employee';
    },
    [create_employee.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.successMessage = payload.message;
      // Add new employee to the list and refresh the entire list
      if (payload.employee) {
        state.employees = [payload.employee, ...state.employees];
      }
    },

    // Update employee
    [update_employee.pending]: (state) => {
      state.loader = true;
    },
    [update_employee.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to update employee';
    },
    [update_employee.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.successMessage = payload.message;
      // Update employee in list
      state.employees = state.employees.map(emp => 
        emp._id === payload.employee._id ? payload.employee : emp
      );
    },

    // Delete employee
    [delete_employee.pending]: (state) => {
      state.loader = true;
    },
    [delete_employee.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to delete employee';
    },
    [delete_employee.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.successMessage = payload.message;
      // Remove employee from list
      state.employees = state.employees.filter(emp => emp._id !== payload.employeeId);
    },

    // Get available modules
    [get_available_modules.pending]: (state) => {
      state.loader = true;
    },
    [get_available_modules.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to fetch modules';
    },
    [get_available_modules.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.modules = payload.modules;
    },

    // Get employee permissions
    [get_employee_permissions.pending]: (state) => {
      state.loader = true;
    },
    [get_employee_permissions.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload?.error || 'Failed to fetch permissions';
    },
    [get_employee_permissions.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.permissions = payload.permissions;
    }
  }
});

export const { messageClear } = employeeReducer.actions;
export default employeeReducer.reducer; 